import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import SwishIcon from "images/icons/svg/v2/swish.svg";
import { formatNumber } from "utils/utils";
import {
  TransactionMethodIcon,
  TransactionSummary,
  TransactionSummaryCard,
  TransactionSummaryIcon,
  TransactionSummaryLabel,
  TransactionSummaryValue,
} from "./tips.transactions.styled";
import type {
  TransactionSummaryFilter,
  TransactionSummaryModel,
} from "./tips.transactions.types";

interface TransactionSummaryCardBaseConfig {
  readonly filter: TransactionSummaryFilter;
  readonly labelKey: string;
  readonly amount: (summary: TransactionSummaryModel) => number;
}

type TransactionSummaryCardConfig =
  | (TransactionSummaryCardBaseConfig & {
      readonly iconName: string;
      readonly type: "customIcon";
    })
  | (TransactionSummaryCardBaseConfig & {
      readonly methodIconSrc: string;
      readonly type: "methodIcon";
    });

interface TipsTransactionsSummaryProps {
  readonly activeSummaryFilter: TransactionSummaryFilter;
  readonly summary: TransactionSummaryModel;
  readonly onSummaryFilterChange: (filter: TransactionSummaryFilter) => void;
}

const transactionSummaryCards: readonly TransactionSummaryCardConfig[] = [
  {
    amount: (summary) => summary.allTransactionsAmount,
    filter: "all",
    iconName: "v2/transfer",
    labelKey: "Tips.AllTransactions",
    type: "customIcon",
  },
  {
    amount: (summary) => summary.depositAmount,
    filter: "deposit",
    iconName: "v2/right-arrow",
    labelKey: "Tips.TotalDeposits",
    type: "customIcon",
  },
  {
    amount: (summary) => summary.withdrawalAmount,
    filter: "withdrawal",
    iconName: "v2/left-arrow",
    labelKey: "Tips.TotalPayouts",
    type: "customIcon",
  },
  {
    amount: (summary) => summary.paidBankAmount,
    filter: "bank",
    iconName: "v2/bank",
    labelKey: "Tips.PaidBank",
    type: "customIcon",
  },
  {
    amount: (summary) => summary.paidSwishAmount,
    filter: "swish",
    labelKey: "Tips.PaidSwish",
    methodIconSrc: SwishIcon,
    type: "methodIcon",
  },
] as const;

export const TipsTransactionsSummary = (
  props: TipsTransactionsSummaryProps,
): JSX.Element => {
  const { activeSummaryFilter, onSummaryFilterChange, summary } = props;
  const { t } = useTranslation();

  return (
    <TransactionSummary>
      {transactionSummaryCards.map((card) => {
        const isActive = activeSummaryFilter === card.filter;

        return (
          <TransactionSummaryCard
            key={card.filter}
            $isPrimary={isActive}
            aria-pressed={isActive}
            type="button"
            onClick={() => onSummaryFilterChange(card.filter)}
          >
            <TransactionSummaryIcon $isPrimary={isActive}>
              {card.type === "customIcon" ? (
                <CustomIcon
                  name={card.iconName}
                  size="20px"
                  color={isActive ? ColorV2.White : ColorV2.Blue1}
                />
              ) : (
                <TransactionMethodIcon
                  src={card.methodIconSrc}
                  alt=""
                  aria-hidden="true"
                />
              )}
            </TransactionSummaryIcon>
            <div>
              <TransactionSummaryValue $isPrimary={isActive}>
                {formatNumber(card.amount(summary))} {t("Tips.Currency")}
              </TransactionSummaryValue>
              <TransactionSummaryLabel $isPrimary={isActive}>
                {t(card.labelKey)}
              </TransactionSummaryLabel>
            </div>
          </TransactionSummaryCard>
        );
      })}
    </TransactionSummary>
  );
};
